import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, CardContent, Typography, Alert, Button,
  CircularProgress, Paper, Collapse, IconButton,
} from '@mui/material';
import { Rule as RulesIcon, Add as AddIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { PageContainer, PageCard, PageLoading, CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { useGetStaffingRulesByFacilityQuery, useUpsertStaffingRuleMutation } from '../../../services/LaborManagementService';
import { hasLaborManagementAdminAccess } from '../../profile/utils/profileUtils';
import { RulesEditorGrid, CreateDefaultRulesDialog, FacilityConfigurationPanel } from '../components/rules';
import { useScopedRuleSave } from '../components/rules/useScopedRuleSave';
import StaleRuleSaveAlert from '../components/rules/StaleRuleSaveAlert';

const LaborRules: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const { handleCenterChange } = useCenterSelection();

  const { data: profile } = useGetMyProfileQuery();
  const isAdmin = hasLaborManagementAdminAccess(profile);

  const skip = !company || !center;
  const { data: staffingRules, isLoading, isError, refetch } = useGetStaffingRulesByFacilityQuery(
    { company, centerId: center },
    { skip },
  );

  const [upsertRule, { isLoading: isUpserting }] = useUpsertStaffingRuleMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(false);

  // Pick the first rule set for the facility (API returns an array)
  const staffingRule = staffingRules?.[0] ?? null;

  const { saveRule, saveSettings, isSaving, conflict, clearConflict } = useScopedRuleSave(staffingRule);

  const handleCreateDefaults = useCallback(async () => {
    setConfirmOpen(false);
    await upsertRule({
      staffingRuleView: { id: '', company, centerId: center, rules: [], operatingDays: [], defaultOperatingDayLengthHours: 8, defaultTraysPerRoom: 1 },
    }).unwrap();
  }, [upsertRule, company, center]);

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <Box sx={{
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <RulesIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                Staffing Rules
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {company} {center && `| ${center}`}
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />
            </Box>

            {/* Help Section */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
                onClick={() => setHelpExpanded(!helpExpanded)}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0369a1' }}>
                  How Staffing Rules Work
                </Typography>
                <IconButton size="small" sx={{ color: '#0369a1' }}>
                  {helpExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={helpExpanded}>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                      Formula Type
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      Determines how staffing is calculated:
                      <strong> Case Based</strong> = staff per daily case count |
                      <strong> Case Duration Based</strong> = staff per case weighted by procedure duration |
                      <strong> Room Based</strong> = staff per operating room |
                      <strong> Tray Based</strong> = staff based on instrument tray volume |
                      <strong> Manual</strong> = fixed headcount
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                      Scaling Factor
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      The primary coefficient in the formula. For Case Based, it's the FTE per case (e.g., 0.286 = 0.286 staff per case).
                      For Room Based, it's staff per OR. For Tray Based, it's the trays-per-tech productivity ratio (default 3.18).
                      For Manual, it's the fixed headcount.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                      Utilization Factor
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      An occupancy/utilization ratio (0.01–1.0) representing the fraction of cases or resources that
                      simultaneously require this role. A value of <strong>1.0</strong> (default) means all cases need this
                      role—no reduction is applied. Lower values reduce staffing because not all cases overlap.
                      For example, <strong>0.85</strong> means only 85% of cases are in this area at once, so the raw
                      staff count is multiplied by 0.85, reducing the result by 15%. Manual formula types ignore this factor.
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#e0f2fe', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#0c4a6e', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                      <strong>Formulas:</strong><br />
                      Case Based: cases × UtilizationFactor × ScalingFactor<br />
                      Case Duration Based: cases × (duration/60) × UtilizationFactor × ScalingFactor<br />
                      Room Based: ScalingFactor × ORs × days × UtilizationFactor<br />
                      Tray Based: trays × UtilizationFactor / ScalingFactor<br />
                      Manual: ScalingFactor
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                      Industry Standard Defaults
                    </Typography>
                    <Box component="table" sx={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse', '& td, & th': { p: 0.75, borderBottom: '1px solid #bae6fd', textAlign: 'left' } }}>
                      <thead>
                        <tr style={{ backgroundColor: '#e0f2fe' }}>
                          <th>Position</th>
                          <th>Formula</th>
                          <th>Factor</th>
                          <th>Ratio</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td>Pre-Op RN</td><td>Case</td><td>0.286</td><td>1 per 3-4 patients</td></tr>
                        <tr><td>PACU RN</td><td>Case Duration</td><td>0.40</td><td>Duration-weighted</td></tr>
                        <tr><td>Patient Care Tech</td><td>Case</td><td>0.111</td><td>1 per 8-10 patients</td></tr>
                        <tr><td>OR Circulating RN</td><td>Room</td><td>1.0</td><td>1 per room (always)</td></tr>
                        <tr><td>OR Scrub Tech</td><td>Room</td><td>1.0</td><td>1 per room</td></tr>
                        <tr><td>X-ray Tech</td><td>Room</td><td>1.0</td><td>1 per room</td></tr>
                        <tr><td>OR Runner/Float</td><td>Room</td><td>0.40</td><td>1 per 2-3 rooms</td></tr>
                        <tr><td>Sterile Processing</td><td>Tray</td><td>3.18</td><td>Trays per tech</td></tr>
                        <tr><td>First Assist</td><td>Room</td><td>1.0</td><td>Disabled by default</td></tr>
                      </tbody>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Paper>

            {!isAdmin && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You do not have admin access to edit staffing rules. Contact your administrator.
              </Alert>
            )}

            {skip && (
              <Alert severity="info">
                Please select a facility from the Labor Management menu.
              </Alert>
            )}

            {isLoading && <PageLoading />}

            {isError && (
              <Alert severity="error">
                Failed to load staffing rules. Please try again.
              </Alert>
            )}

            <StaleRuleSaveAlert conflict={conflict} onReload={() => { clearConflict(); refetch(); }} />

            {!isLoading && !isError && !skip && !staffingRule && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No staffing rules found for this facility.
                </Typography>
                {isAdmin && (
                  <Button
                    variant="contained"
                    startIcon={isUpserting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                    onClick={() => setConfirmOpen(true)}
                    disabled={isUpserting}
                    sx={{
                      backgroundColor: '#3b82f6',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 3,
                      '&:hover': { backgroundColor: '#2563eb' },
                    }}
                  >
                    {isUpserting ? 'Creating…' : 'Create Default Rules'}
                  </Button>
                )}
              </Box>
            )}

            <CreateDefaultRulesDialog
              open={confirmOpen}
              company={company}
              center={center}
              onClose={() => setConfirmOpen(false)}
              onConfirm={handleCreateDefaults}
            />

            {staffingRule && (
              <>
                <FacilityConfigurationPanel
                  staffingRule={staffingRule}
                  isSaving={isSaving}
                  isAdmin={isAdmin}
                  onSaveSettings={saveSettings}
                />
                <RulesEditorGrid
                  staffingRule={staffingRule}
                  onSaveRule={saveRule}
                  isSaving={isSaving}
                  readOnly={!isAdmin}
                />
              </>
            )}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default LaborRules;
